import React, { useEffect } from 'react';
import cn from 'classnames';
import SVG, { SVGProps } from './SVGWrapper';
import { inOutQuad, Point, ptsAtProgress } from '@support/animation';
import styles from './Icons.module.scss';
import Contact from '@models/Contact';
import { useSelector } from 'react-redux';
import { selectReconnectCadenceType } from '@actions/reconnect';
import { useTween } from '@hooks/use-tween';
import Group, { GroupReconnectCadence } from '@models/Group';
import { selectGroupReconnectCadence } from '@actions/groups';
import { CadenceType } from '@models/Cadence';

const noCadencePts: Array<Point> = [
  { x: 10.5, y: 14.5 },
  { x: 2.1, y: 2.1 },
  { x: 3, y: -3 },
  { x: 0, y: 0 },
  { x: 2.1, y: 2.1 },
  { x: -3, y: 3 },
];

const hasCadencePts: Array<Point> = [
  { x: 3.3, y: 5.6 },
  { x: 0.8, y: 0.8 },
  { x: 1.3, y: -1.2 },
  { x: 16.05, y: 16.05 },
  { x: 0.8, y: 0.8 },
  { x: -1.3, y: 1.2 },
];

const easingScheme = [inOutQuad, inOutQuad, inOutQuad, inOutQuad, inOutQuad, inOutQuad];

export const ReconnectIcon = ({
  width = 24,
  height = 24,
  color = 'primary',
  hoverOpacity = true,
  contact,
  group,
  ...props
}: SVGProps & { contact?: Contact; group?: Group }) => {
  let cadenceType: CadenceType | GroupReconnectCadence = useSelector(
    selectReconnectCadenceType(contact?.id),
  );
  if (group) {
    cadenceType = useSelector(selectGroupReconnectCadence(group.id));
  }

  const hasCadence = !['none', 'disabled'].includes(cadenceType);

  const [animationProgress, resetTween] = useTween({
    startingValue: hasCadence ? 1 : 0,
    targetValue: hasCadence ? 1 : 0,
    duration: 0.2,
    easingFn: inOutQuad,
  });

  const pts = ptsAtProgress(animationProgress, 0, 1, hasCadencePts, noCadencePts, easingScheme);

  useEffect(() => {
    if (hasCadence && animationProgress < 1) {
      resetTween(1);
    } else if (!hasCadence && animationProgress > 0) {
      resetTween(0);
    }
  }, [hasCadence, animationProgress]);

  return (
    <SVG
      {...{ ...props, width, height, color, hoverOpacity }}
      className={cn(
        'icon action-reconnect-icon',
        props.className,
        styles.reconnect,
        !hasCadence && styles.noCadence,
        hasCadence && cadenceType != 'auto' && styles.userSetCadence,
      )}
      viewBox="0 0 24 24"
    >
      <defs>
        <path
          id="inner"
          strokeWidth="1.8"
          fill="none"
          d="M15,17.2 C16.3,16.3 17.2,14.7 17.2,13 C17.2,10.1 14.9,7.8 12,7.8 C9.1,7.8 6.8,10.1 6.8,13 C6.8,14.8 7.7,16.3 9,17.2"
        ></path>
        <path
          id="outer"
          strokeWidth="1.8"
          fill="none"
          d="M17.6,20 C19.7,18.4 21,15.8 21,13 C21,8 17,4 12,4 C7,4 3,8 3,13 C3,15.8 4.3,18.4 6.4,20"
        ></path>
      </defs>
      <g strokeWidth="1.8" strokeLinecap="round">
        <use className={cn(styles.inner, styles.left, styles.ring)} href="#inner" />
        <use className={cn(styles.inner, styles.right, styles.ring)} href="#inner" />
        <use className={cn(styles.inner, styles.right2, styles.ring)} href="#inner" />

        <use className={cn(styles.outer, styles.left, styles.ring)} href="#outer" />
        <use className={cn(styles.outer, styles.right, styles.ring)} href="#outer" />
        <use className={cn(styles.outer, styles.right2, styles.ring)} href="#outer" />

        <path
          stroke="none"
          d={`M${pts[0].x} ${pts[0].y}
              a${pts[1].x} ${pts[1].y} 0 0 1 ${pts[2].x} ${pts[2].y}
              l${pts[3].x} ${pts[3].y}
              a${pts[4].x} ${pts[4].y} 0 0 1 ${pts[5].x} ${pts[5].y}`}
        />
      </g>
    </SVG>
  );
};

export const AutoReconnectIcon = ({
  width = 24,
  height = 24,
  color = 'primary',
  hoverOpacity = true,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color, hoverOpacity }}
    className={cn('icon action-reconnect-icon', props.className)}
    viewBox="0 0 24 24"
  >
    <g strokeWidth="1.8" strokeLinecap="round">
      <circle stroke="none" cx="12" cy="13" r="2.1"></circle>
      <path
        fill="none"
        d="M15,17.2 C16.3,16.3 17.2,14.7 17.2,13 C17.2,10.1 14.9,7.8 12,7.8 C9.1,7.8 6.8,10.1 6.8,13 C6.8,14.8 7.7,16.3 9,17.2"
      ></path>
      <path
        fill="none"
        d="M17.6,20 C19.7,18.4 21,15.8 21,13 C21,8 17,4 12,4 C7,4 3,8 3,13 C3,15.8 4.3,18.4 6.4,20"
      ></path>
    </g>
  </SVG>
);

export const NoReconnectIcon = ({
  width = 24,
  height = 24,
  color = 'primary',
  hoverOpacity = true,
  ...props
}: SVGProps) => (
  <SVG
    {...{ ...props, width, height, color, hoverOpacity }}
    className={cn('icon no-reconnect-icon', props.className)}
    viewBox="0 0 24 24"
  >
    <path
      fill="none"
      strokeWidth="1.8"
      strokeLinecap="round"
      d="M17.2,13.3 C17.2,13.2 17.2,13.1 17.2,13 C17.2,10.1 14.9,7.8 12,7.8 C11.9,7.8 11.7,7.8 11.6,7.8 M6.8,12.6 C6.8,12.8 6.8,12.9 6.8,13 C6.8,14.8 7.7,16.3 9,17.2 M20.3,16.5 C20.7,15.4 21,14.2 21,13 C21,8 17,4 12,4 C10.8,4 9.6,4.3 8.5,4.7 M3.7,9.6 C3.2,10.7 3,11.8 3,13 C3,15.8 4.3,18.4 6.4,20 M4 5l16 16"
    />
  </SVG>
);
